<script setup>
import { ref, onMounted, watch } from "vue";
import { storeToRefs } from "pinia";
import { useNotificacoesPopoverStore } from "@/stores/notificacoesPopover";
import { useNotificacoesStore } from "@/stores/notificacoes";
import { useEauthWidget } from "@/composables/useEauthWidget";
import { useBreakpoint } from "@/composables/useBreakpoint";
import { useAppNavigation } from "@/composables/navigation/useAppNavigation";
import { useNotificacaoNavigation } from "@/composables/navigation/useNotificacaoNavigation";
import { usePerfilNavigation } from "@/composables/navigation/usePerfilNavigation";
import { useAuthStore } from "@/stores/auth";
import Button from "primevue/button";
import Menu from "primevue/menu";
import Popover from "primevue/popover";
import BotaoMenuLateral from "@/vue/components/BotaoMenuLateral.vue";
import Notificacoes from "@/vue/components/Notificacoes.vue";
import OverlayBadge from "primevue/overlaybadge";

const props = defineProps({
    height: {
        type: String,
        default: '60px'
    }
});

// USER
const authStore = useAuthStore();

const { user, isAuthenticated } = storeToRefs(authStore);

// WIDGET EAUTH
const widgetEauthContainer = ref('widget-eauth-container');

const { mountWidget } = useEauthWidget();

// MENU PERFIL
const menu = ref();

// NOTIFICAÇÕES
const notificacoesStore = useNotificacoesStore();

const { notificacoesNaoLidasCount } = storeToRefs(notificacoesStore);

// POPOVER NOTIFICAÇÕES
const notificacoesPopoverStore = useNotificacoesPopoverStore();

const notificacoesPopover = ref();

// NAVEGAÇÃO
const { goToHome } = useAppNavigation();

const { goToNotificacoes } = useNotificacaoNavigation();

const { goToPerfil } = usePerfilNavigation();

const toggleNotificacoesPopover = (event) => {
    if (isMobile.value) {
        goToNotificacoes();

        return;
    }

    notificacoesPopover.value.toggle(event);
}

// PERFIL POPOVER
const itensMenuPerfil = ref([
    {
        label: 'Minha Conta',
        icon: 'pi pi-user',
        command: () => {
            goToPerfil();
        }
    },
    {
        separator: true
    },
    {
        label: 'Sair',
        icon: 'pi pi-sign-out',
        severity: 'danger',
        command: () => authStore.logout()
    }
]);

const togglePerfilPopover = (event) => {
    menu.value.toggle(event);
}

// MOBILE/DESKTOP
const { isMobile } = useBreakpoint();

// STYLES
const heightBarraSuperior = ref(props.height);
//

onMounted(() => {
    montarWidget();
});

const montarWidget = () => {
    mountWidget({
        containerId: widgetEauthContainer.value,
        buttonSize: '49',
        iconSize: '25',
        buttonBackground: 'var(--color-topbar)'
    });
}

watch(
    () => notificacoesPopoverStore.closeNotificacoesPopover,
    (shouldClose) => {
        if (shouldClose) {
            notificacoesPopover.value?.hide();
            notificacoesPopoverStore.resetCloseNotificacoesPopover();
        }
    }
);
</script>

<template>
    <div>
        <div :style="`width: 100vw; height: ${heightBarraSuperior}; min-height: ${heightBarraSuperior};`"></div>

        <div class="top-bar">
            <div class="d-flex align-items-center justify-content-center gap-2">
                <Button
                    v-tooltip.bottom="{ value: 'Início', showDelay: 100 }"
                    icon="pi pi-home"
                    rounded
                    text
                    pt:root:class="secondary-button-root"
                    pt:icon:class="secondary-button-icon"
                    @click="goToHome()"
                />

                <BotaoMenuLateral
                    v-tooltip.bottom="{ value: 'Menu', showDelay: 100 }"
                    title=""
                    size=""
                    rounded
                    text
                    button-class="secondary-button-root"
                    icon-class="secondary-button-icon"
                />
            </div>

            <div class="d-flex align-items-center justify-content-center gap-2">
                <!-- BOTÃO WIDGET EAUTH -->
                <div :id="widgetEauthContainer"></div>

                <!-- BOTÃO NOTIFICAÇÕES -->
                <div v-if="isAuthenticated">
                    <Button
                        v-if="notificacoesNaoLidasCount === 0"
                        v-tooltip.bottom="{ value: 'Notificações', showDelay: 100 }"
                        icon="pi pi-bell"
                        size="large"
                        rounded
                        text
                        pt:root:class="secondary-button-root"
                        pt:icon:class="secondary-button-icon"
                        @click="toggleNotificacoesPopover($event)"
                    />

                    <OverlayBadge
                        v-else
                        :value="notificacoesNaoLidasCount"
                        severity="danger"
                        size="small"
                    >
                        <Button
                            v-tooltip.bottom="{ value: 'Notificações', showDelay: 100 }"
                            icon="pi pi-bell"
                            size="large"
                            rounded
                            text
                            pt:root:class="secondary-button-root"
                            pt:icon:class="secondary-button-icon"
                            @click="toggleNotificacoesPopover($event)"
                        />
                    </OverlayBadge>
                </div>

                <!-- POPOVER NOTIFICAÇÕES -->
                <Popover
                    ref="notificacoesPopover"
                    :style="{
                        width: '400px',
                        maxHeight: '500px',
                        overflowY: 'auto'
                    }"
                    :pt:root:style="{
                        display: 'flex',
                        background: 'transparent',
                        border: 'none',
                        borderRadius: '8px',
                        boxShadow: '0px 0px 17px -6px rgb(0 0 0 / 30%)'
                    }"
                    :pt:content:style="{ width: '100%', padding: '0', display: 'flex' }"
                >
                    <Notificacoes :compact="true"/>
                </Popover>

                <!-- BOTÃO ENTRAR -->
                <Button
                    v-if="!isAuthenticated"
                    v-tooltip.bottom="{ value: 'Acesse sua conta', showDelay: 100 }"
                    label="Entrar"
                    icon="pi pi-user"
                    rounded
                    text
                    pt:icon:class="secondary-button-icon"
                    @click="authStore.login()"
                />

                <!-- BOTÃO PERFIL -->
                <Button
                    v-if="isAuthenticated"
                    v-tooltip.bottom="{
                        value: 'Perfil',
                        showDelay: 100,
                        pt: { text: { style: 'width: fit-content; white-space: nowrap;' } }
                    }"
                    icon="pi pi-user"
                    size="large"
                    rounded
                    text
                    aria-haspopup="true"
                    aria-controls="overlay_menu"
                    pt:root:class="secondary-button-root"
                    pt:icon:class="secondary-button-icon"
                    @click="togglePerfilPopover($event)"
                />

                <!-- POPOVER PERFIL -->
                <Menu ref="menu" id="overlay_menu" :model="itensMenuPerfil" :popup="true" style="max-width: 300px">
                    <template #start>
                        <h5 class="nome-usuario">
                            {{ user.name }}
                        </h5>
                    </template>
                </Menu>
            </div>
        </div>
    </div>
</template>

<style scoped>
.top-bar {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: v-bind(heightBarraSuperior);
    padding: 25px 10px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
    background: var(--color-topbar);
    color: var(--color-text-light);
    z-index: 501;
    box-shadow: 0 0 5px 0 rgb(0 0 0 / 30%);
}

.nome-usuario {
    margin: 0;
    padding: 8px 13px;
    color: var(--color-text);
    font-weight: 600;
    word-break: break-word;
}

:deep(.secondary-button-root) {
    width: 50px !important;
    min-width: 50px !important;
    height: 50px !important;
    min-height: 50px !important;
}

:deep(.secondary-button-root:hover) {
    background: var(--color-primary-hover) !important;
}

:deep(.secondary-button-icon) {
    color: var(--color-text-light);
    font-size: 1.3rem;
}

:deep(.p-overlaybadge .p-badge) {
    transform: translate(15%, 10%);
    outline: none;
}
</style>
